import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { SelectDialogSearchField } from '../../../common/dialogs/SelectDialog';
import { isVersionEqual } from '../../../common/version-item/Version.helpers';
import { VersionItem } from '../../../common/version-item/Version.interface';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { useFindFilesPaginated } from '../Document.hook';
import { FileDto } from '../Document.interface';
import DocumentVersionDialogPicker from './DocumentVersionDialogPicker';

const StyledDialogContent = styled(DialogContent)`
  padding: 8px 0 0;
  .pagination {
    bottom: 0;
    position: sticky;
    background-color: #fff;
  }
`;

const StyledTableContainer = styled(TableContainer)`
  .row-divider {
    background-color: ${props => props.theme.palette.grey[100]};
    .MuiTableCell-root {
      padding-top: 0px;
      padding-bottom: 0px;
    }
  }
`;

const columns: { key: string; label: string }[] = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Name' },
  { key: 'version', label: 'Version' },
  { key: 'task.status', label: 'Status' }
];

export interface AssignDocumentVersionDialogProps {
  open: boolean;
  items?: VersionItem[];
  title?: React.ReactNode;
  content?: React.ReactNode;
  searchLabel?: string;
  onCancel?: () => void;
  onAssign?: (items: VersionItem[]) => void;
}

const AssignDocumentVersionDialog: React.FC<
  AssignDocumentVersionDialogProps
> = props => {
  const {
    open,
    items,
    title = 'Assign Document',
    searchLabel = 'Search Item(s)',
    content = 'Search for Document to assign.',
    onCancel,
    onAssign
  } = props;
  const defaultLimit = 5;
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);
  const [order, setOrder] = useState<string>();
  const [direction, setDirection] = useState<'ASC' | 'DESC'>();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => items || []);
  const [selectedFile, setSelectedFile] = useState<FileDto>();
  const [{ data, isLoading, error }, findFiles] = useFindFilesPaginated();
  const colSpan = columns.length + +(selected.length > 0);

  const files = useMemo(() => data?.data || [], [data]);

  const isActive = (column: string) => column === order;

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return isActive(column) && direction === 'DESC' ? 'desc' : 'asc';
  };

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const findFilesWithParams = useCallback(() => {
    findFiles({ page, limit, order, direction, search });
  }, [page, limit, order, direction, search, findFiles]);

  // clear state
  useEffect(() => {
    if (!open) {
      setPage(0);
      setLimit(defaultLimit);
      setOrder(undefined);
      setDirection(undefined);
      setSearch('');
      setSelected([]);
    }
  }, [open]);

  useEffect(() => {
    open && findFilesWithParams();
  }, [open, findFilesWithParams]);

  useEffect(() => {
    open && items && setSelected(items);
  }, [items, open]);

  useEffect(() => {
    items && setSelected(items);
  }, [items]);

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <StyledDialogContent>
        <Box mx={3}>
          <DialogContentText>{content}</DialogContentText>
          <SelectDialogSearchField
            label={searchLabel}
            onSearch={value => {
              setPage(0);
              setSearch(value);
            }}
          />
        </Box>
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                {columns.map((column, index) => (
                  <TableCell key={`${column.key}-${index}`}>
                    <TableSortLabel
                      active={isActive(column.key)}
                      direction={getSortDirection(column.key)}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {selected.length > 0 && (
                <>
                  <TableRow className='row-divider'>
                    <TableCell colSpan={colSpan}>
                      <Typography variant='overline'>Selected Files</Typography>
                    </TableCell>
                  </TableRow>
                  {selected.map((item, index) => (
                    <TableRow
                      hover
                      selected
                      color='primary'
                      key={`${item.id}-${item.version}-${index}`}
                      onClick={() => {
                        // remove selected
                        setSelected(items => {
                          return items.filter(i => !isVersionEqual(i, item));
                        });
                      }}
                    >
                      <TableCell padding='checkbox'>
                        <Checkbox checked color='primary' />
                      </TableCell>
                      <TableCell>{item.task.key || `DOC-${item.id}`}</TableCell>
                      <WrappedTableCell>{item.name}</WrappedTableCell>
                      <TableCell>{item.version}</TableCell>
                      <TableCell>
                        <TaskStatusLabel task={item.task} />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className='row-divider'>
                    <TableCell colSpan={colSpan}>
                      <Typography variant='overline'>
                        Available Files
                      </Typography>
                    </TableCell>
                  </TableRow>
                </>
              )}
              {isLoading && (
                <TableRow>
                  <TableCell padding='none' colSpan={colSpan}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {error && (
                <TableRow>
                  <TableCell padding='none' colSpan={colSpan}>
                    <Alert severity='error'>{error.message}</Alert>
                  </TableCell>
                </TableRow>
              )}
              {files.length === 0 && (
                <TableRow>
                  <TableCell colSpan={colSpan}>
                    <Typography variant='body1'>
                      {isLoading ? 'Loading files...' : 'No available files.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {files.map((file, index) => (
                <TableRow
                  hover
                  color='primary'
                  key={`${file.id}-${file.version}-${index}`}
                  onClick={() => setSelectedFile(file)}
                >
                  <TableCell padding='checkbox'>
                    <Checkbox color='primary' checked={false} />
                  </TableCell>
                  <TableCell>{file.task?.key || `DOC-${file.id}`}</TableCell>
                  <WrappedTableCell>{file.name}</WrappedTableCell>
                  <TableCell>
                    {file.task?.versionLog?.version ||
                      file.task?.version ||
                      file.version ||
                      1}
                  </TableCell>
                  <TableCell>
                    <TaskStatusLabel task={file.task} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
        <TablePagination
          className='pagination'
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={data?.total || 0}
          rowsPerPage={limit}
          page={page}
          onPageChange={(_, page) => setPage(page)}
          onRowsPerPageChange={event => {
            setLimit(+event.target.value);
            setPage(0);
          }}
        />
      </StyledDialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => onAssign?.(selected)}
        >
          Assign
        </Button>
      </DialogActions>
      {selectedFile && (
        <DocumentVersionDialogPicker
          open
          multiple
          items={selected}
          file={selectedFile}
          onCancel={() => setSelectedFile(undefined)}
          onSelect={newItems => {
            const fileId = selectedFile.id;
            setSelectedFile(undefined);
            setSelected(items => {
              // only check same id, filter out existing items that are not selected
              const allItems = items.filter(item => {
                return (
                  item.id !== fileId ||
                  newItems.some(newItem => isVersionEqual(newItem, item))
                );
              });
              // save selected to items without duplicating
              const includedItems = newItems.filter(newItem => {
                return !allItems.some(item => isVersionEqual(item, newItem));
              });
              return allItems.concat(includedItems);
            });
          }}
        />
      )}
    </Dialog>
  );
};

export default AssignDocumentVersionDialog;
